// services/dismissal.js — сметки и срокове около прекратяване на трудовия
// договор: обезщетение при незаконно уволнение (чл. 225 КТ), задържани
// документи (чл. 226, ал. 2 КТ), срок за изплащане на обезщетенията
// (чл. 228, ал. 3 КТ), давности по трудови спорове (чл. 358 КТ) и срок
// за изпитване (чл. 70–71 КТ). Чисти функции; работни дни — само през
// holidays.js. Нормативните текстове са сверени в актуалната им редакция
// към 08.2026 — вкл. ограничението „до 1 месец изпитване при срочен
// договор под 1 година" (ДВ 62/2022).

const holidays = require('./holidays');
const { round2 } = require('./totals');

// ---- календарни помощници (UTC, без часови зони) ----

function parts(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return { y, m, d };
}
function pad2(n) { return (n < 10 ? '0' : '') + n; }
function fmt(y, m, d) { return y + '-' + pad2(m) + '-' + pad2(d); }
function daysInMonth(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }

// Добавя месеци „по число": 15.08 + 1 м. = 15.09. Ако числото го няма в
// целевия месец (31.01 + 1 м.), се захваща последният ден — така смятат
// сроковете и съдилищата (чл. 72 ЗЗД по аналогия).
function addMonthsClamped(iso, months) {
  const p = parts(iso);
  const total = p.y * 12 + (p.m - 1) + months;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return fmt(y, m, Math.min(p.d, daysInMonth(y, m)));
}

// ---- А4. Незаконно уволнение (чл. 225 КТ) ----

// Таванът е 6 месеца и е нормативен, не настройка.
const UNFAIR_DISMISSAL_MAX_MONTHS = 6;

// Обезщетение за оставане без работа поради признато за незаконно
// уволнение. При постъпване на ПО-НИСКОПЛАТЕНА работа (ал. 2) се дължи
// само разликата в заплатите — пак до 6 месеца.
function unfairDismissalPay(o) {
  const monthsOut = Number(o.monthsOutOfWork) || 0;
  const monthsPaid = Math.min(monthsOut, UNFAIR_DISMISSAL_MAX_MONTHS);
  const gross = Number(o.monthlyGross) || 0;
  const perMonth = o.lowerPaid ? gross - (Number(o.lowerPaidMonthly) || 0) : gross;
  const notes = [
    'Базата е брутното възнаграждение за последния пълен отработен месец (чл. 228, ал. 1).',
    'Времето се признава за трудов стаж (чл. 354, ал. 1, т. 1).',
  ];
  if (o.lowerPaid) notes.unshift('По-нископлатена работа → дължи се само разликата в заплатите (ал. 2).');
  return {
    monthsPaid,
    capped: monthsOut > UNFAIR_DISMISSAL_MAX_MONTHS,
    perMonth: round2(perMonth),
    amount: round2(perMonth * monthsPaid),
    notes,
  };
}

// ---- А7. Невписано прекратяване / документи (чл. 226, ал. 2 КТ) ----

// ВНИМАНИЕ, нова редакция от 01.06.2025 (електронния трудов запис):
// „незаконно задържане на трудовата книжка" е заменено с „невписване на
// прекратяването на трудовия договор по реда на чл. 62, ал. 3". Обезщетението
// е брутното възнаграждение от деня на прекратяването до ВПИСВАНЕТО в
// регистъра на заетостта. Дневната база е БТВ / отработените дни на
// базовия месец; дните на забавата се броят като РАБОТНИ дни.
function unregisteredTerminationPay(o) {
  const notes = [
    'Дължи се за времето от прекратяването до вписването му в регистъра на заетостта (чл. 226, ал. 2 и 3, ред. от 01.06.2025).',
    'Същата сметка важи и за неиздадени/забавени документи и за неоформена хартиена книжка от преди 06.2025.',
  ];
  // Празни „отработени дни" НЕ стават делител 1 (преглед 17.09.2026): при
  // 1500 € и празно поле сметката излизаше 30 000 € вместо 1 428,57 € — без
  // никакво предупреждение. Без дни резултатът е 0 с обяснение.
  const baseDays = Number(o.baseWorkDays);
  if (!Number.isFinite(baseDays) || baseDays <= 0) {
    notes.unshift('Въведете отработените дни в базовия месец — без тях дневната база не може да се сметне.');
    return { dailyRate: 0, workDays: 0, amount: 0, notes };
  }
  const dailyRate = (Number(o.baseMonthlyGross) || 0) / baseDays;
  const workDays = holidays.workingDaysBetween(o.from, o.to, o.overrides);
  return {
    dailyRate: round2(dailyRate),
    workDays,
    amount: round2(dailyRate * workDays),
    notes,
  };
}

// ---- Б2. Срок за изплащане на обезщетенията (чл. 228, ал. 3 КТ) ----

// Обезщетенията при прекратяване се изплащат не по-късно от последния
// ден на месеца, СЛЕДВАЩ месеца на прекратяването, освен ако в КТД е
// договорен друг срок. След изтичането му се дължи и законната лихва.
function payDeadline(o) {
  const p = parts(o.terminatedOn);
  const nm = p.m === 12 ? { y: p.y + 1, m: 1 } : { y: p.y, m: p.m + 1 };
  return {
    deadline: fmt(nm.y, nm.m, daysInMonth(nm.y, nm.m)),
    notes: [
      'Ако в колективния трудов договор (КТД) е договорен друг срок — важи той.',
      'След изтичане на срока се дължи обезщетението заедно със законната лихва.',
    ],
  };
}

// ---- Б3. Давности по трудови спорове (чл. 358 КТ) ----

// Видовете искове и срокът им са конфигурация с правното основание до
// всяка стойност, не заровени числа из кода.
const CLAIM_KINDS = {
  liability: {
    months: 1,
    label: 'Ограничена имуществена отговорност / отмяна на наказание „забележка"',
    startFrom: 'от връчването на заповедта, съответно от деня на плащането',
  },
  dismissal: {
    months: 2,
    label: 'Прекратяване на трудовия договор (искове по чл. 344, ал. 1, т. 1–3) и „предупреждение за уволнение"',
    startFrom: 'от деня на прекратяването, съответно от връчването на заповедта',
  },
  money: {
    months: 36,
    label: 'Парични вземания и всички останали трудови спорове (3 години)',
    startFrom: 'от деня, в който вземането е станало изискуемо',
  },
};

function claimDeadline(o) {
  const kind = CLAIM_KINDS[o.kind];
  if (!kind) throw new Error('Непознат вид иск: ' + o.kind);
  return {
    deadline: addMonthsClamped(o.startsOn, kind.months),
    months: kind.months,
    notes: ['Срокът тече ' + kind.startFrom + ' (чл. 358, ал. 2).'],
  };
}

// ---- Б4. Срок за изпитване (чл. 70–71 КТ) ----

const PROBATION_MAX_MONTHS = 6;      // чл. 70, ал. 1
const PROBATION_SHORT_MONTHS = 1;    // при срочен договор под 1 година (ДВ 62/2022)

// Кога изтича уговореният срок за изпитване. Отсъствията в законоустановен
// отпуск (платен, неплатен, болничен) НЕ се броят (чл. 70, ал. 4) — те
// удължават срока ден за ден.
function probationEnd(o) {
  const wanted = Number(o.months) || PROBATION_MAX_MONTHS;
  // „Срочен под 1 година" е само ИСТИНСКИ срок 1–11 месеца (преглед
  // 17.09.2026): празното поле и нулата (Number('') === 0) правеха
  // безсрочния договор „срочен" и сваляха тавана от 6 месеца на 1.
  const cm = Number(o.contractMonths);
  const shortContract = Number.isFinite(cm) && cm > 0 && cm < 12;
  const cap = shortContract ? PROBATION_SHORT_MONTHS : PROBATION_MAX_MONTHS;
  const monthsApplied = Math.min(wanted, cap);
  const absent = Number(o.absentDays) || 0;
  // срокът от 01.02 „за 1 месец" изтича на 28.02 — последният ден вътре
  const base = holidays.addDays(addMonthsClamped(o.startsOn, monthsApplied), -1);
  const notes = [];
  if (shortContract && wanted > PROBATION_SHORT_MONTHS) {
    notes.push('Срочен договор под 1 година → изпитването е най-много 1 месец (чл. 70, ал. 1, ДВ 62/2022).');
  } else if (wanted > PROBATION_MAX_MONTHS) {
    notes.push('Уговорка над 6 месеца е недействителна — важат 6 месеца (чл. 70, ал. 1).');
  }
  if (absent > 0) notes.push('Дните в законоустановен отпуск не се броят (чл. 70, ал. 4) — срокът се удължава с ' + absent + ' дни.');
  notes.push('До изтичането всяка от страните, в чиято полза е уговорен, може да прекрати без предизвестие (чл. 71, ал. 1).');
  return { monthsApplied, endsOn: holidays.addDays(base, absent), notes };
}

module.exports = {
  unfairDismissalPay, unregisteredTerminationPay, payDeadline, claimDeadline, probationEnd,
  CLAIM_KINDS, UNFAIR_DISMISSAL_MAX_MONTHS,
  // изнесени за family.js и тестовете — едно смятане на месеци за всички
  addMonthsClamped,
};
